import { BookOpen } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@quebon/ui/components/dialog'
import type { MaterialNotice } from '@/api/lcLearning'
import { learnerBookTitle } from '@/utils/learnerBookTitle'
import { Button } from '../ui/Button'

interface Props {
  open: boolean
  notice: MaterialNotice
  onConfirm: () => void
}

export function MaterialBookOpenDialog({ open, notice, onConfirm }: Props) {
  const { t } = useTranslation(['learner', 'common'])
  const bookName = learnerBookTitle(notice.title)
  const instruction = notice.blockQuizStartPage != null
    ? t('video.materialBookDialog.titleWithPage', {
        bookName,
        page: notice.blockQuizStartPage,
      })
    : t('video.materialBookDialog.titleWithoutPage', { bookName })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onConfirm()
      }}
    >
      <DialogContent
        showCloseButton={false}
        className="w-[calc(100%-32px)] max-w-[360px] gap-6 rounded-3xl border-0 bg-lc-bg-primary px-6 pb-6 pt-8 shadow-[0_8px_8px_rgba(0,0,0,0.1)]"
      >
        {notice.learnerCoverImageUrl ? (
          <img
            src={notice.learnerCoverImageUrl}
            alt=""
            className="mx-auto h-[200px] w-[140px] rounded-2xl object-contain shadow-[0_8px_16px_rgba(0,0,0,0.1)]"
          />
        ) : (
          <div
            data-testid="material-cover-fallback"
            className="mx-auto flex h-[200px] w-[140px] items-center justify-center rounded-2xl border-2 border-black/10 bg-lc-v3-gray-100 text-lc-v3-gray-400 shadow-[0_8px_16px_rgba(0,0,0,0.1)]"
            aria-label={t('video.materialBookDialog.coverFallback') as string}
          >
            <BookOpen className="size-12" strokeWidth={1.5} aria-hidden />
          </div>
        )}

        <DialogHeader className="items-center gap-4 text-center">
          <DialogTitle className="text-lc-v3-h4 font-bold text-lc-text-primary">
            {instruction}
          </DialogTitle>
          <p className="text-lc-v3-body-1 font-normal text-lc-text-secondary">
            {t('video.materialBookDialog.description')}
          </p>
        </DialogHeader>

        <Button variant="primary" size="L" className="mx-auto min-w-[104px]" onClick={onConfirm}>
          {t('common:button.confirm')}
        </Button>
      </DialogContent>
    </Dialog>
  )
}
